<script lang="ts">

    import 'mapbox-gl/dist/mapbox-gl.css';
    import { AnimationOptions, AnySourceData, CameraOptions, Map as Mapgl, MapboxOptions, MapTouchEvent } from 'mapbox-gl';
    import type { LngLatBoundsLike } from 'mapbox-gl';
    import { createEventDispatcher, onMount, setContext } from 'svelte';
    import { readable, writable } from 'svelte/store';
    import { MAPBOX_ACCESS_KEY, MAPBOX_STYLE } from '../environment';
    import type { LonLat } from '../global';
    
    export let map: Mapgl | null = null;
    export let bounds: LngLatBoundsLike = undefined;
    export let satellite = false;

    const dispatch = createEventDispatcher();
    const mapWritable = writable<Mapgl>(null);
    const mapReadable = readable<Mapgl>(null, set => mapWritable.subscribe(set));

    let mapContainer: HTMLElement;
    let mapIsReady = false;
    let mapIsLoaded: Promise<void>;

    $: if (map && bounds) {
        map.fitBounds(bounds);
    }
    $: toggleSatellite(map, satellite);

    setContext('map', mapReadable);

    onMount(() => {

        const options: MapboxOptions = {
            accessToken: MAPBOX_ACCESS_KEY,
            container: mapContainer,
            style: MAPBOX_STYLE,
        };

        if (bounds) {
            options.bounds = bounds;
        } else {
            options.center = [-16.514362650910837, 28.298957338073354];
            options.zoom = 10;
        }

        map = new Mapgl(options);

        map.on('dragstart', () => dispatch('dragstart'));
        map.on('movestart', () => dispatch('movestart'));
        map.on('moveend', () => dispatch('moveend'));
        map.on('touchstart', touchstartHandler);
        map.on('touchstart', touchstartHandler);
        map.on('touchmove', touchmoveHandler);
        map.on('touchend', touchendHandler);
        map.on('mousedown', () => dispatch('mousedown'));

        mapIsLoaded = new Promise(resolve => map.once('load', () => {
            mapIsReady = true;

            const dummySource: AnySourceData = {
                type: 'geojson',
                data: {
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [0, 0]
                    },
                    properties: {}
                }
            };

            //Añadimos 3 layers de referencia para poder
            //añadir luego layers en un orden de renderizado apropiado
            
            map.addLayer({
                id: 'background-layer',
                type: 'circle',
                layout: { visibility: 'none' },
                source: dummySource
            });

            map.addLayer({
                id: 'low-layer',
                type: 'circle',
                layout: { visibility: 'none' },
                source: dummySource
            });

            map.addLayer({
                id: 'high-layer',
                type: 'circle',
                layout: { visibility: 'none' },
                source: dummySource
            });

            mapWritable.set(map);
            resolve();
        }));

        //Llamamos a resize un segundo despues de que el componente
        //haya montado para contemplar posibles cambios del layout
        setTimeout(() => map.resize(), 1000);

    });

    async function toggleSatellite(map: Mapgl, useSatellite: boolean): Promise<void> {
        if (!map) {
            return;
        }

        await mapIsLoaded;

        if (!useSatellite) {
            if (map.getLayer('custom-satellite')) {
                map.removeLayer('custom-satellite');
                map.removeSource('custom-satellite');
            }
            return;
        }
        
        if (map.getLayer('custom-satellite')) {
            return;
        }

        map.addSource('custom-satellite', {
            type: "raster",
            url: "mapbox://mapbox.satellite",
            tileSize: 256
        });

        map.addLayer({
            id: 'custom-satellite',
            type: 'raster',
            source: 'custom-satellite',
        }, 'background-layer');

    }

    function touchstartHandler(event: MapTouchEvent) {
        dispatch('touchstart', event);
    }

    function touchmoveHandler(event: MapTouchEvent) {
        dispatch('touchmove', event);
    }

    function touchendHandler(event: MapTouchEvent) {
        dispatch('touchend', event);
    }

    export function setCenter([lon, lat]: LonLat): void {
        const make = () => map.setCenter({ lon, lat });
        if (mapIsReady) {
            make();
        } else {
            map.once('load', make);
        }
    }

    export function panTo([lon, lat]: LonLat, options?: AnimationOptions): Promise<void> {
        return new Promise(resolve => {
            const make = () => {
                map.once('moveend', () => resolve());
                map.panTo({ lon, lat }, options);
            };
            if (mapIsReady) {
                make();
            } else {
                map.once('load', make);
            }
        });
    }

    export function jumpTo(options: CameraOptions): Promise<void> {
        return new Promise(resolve => {
            const make = () => {
                map.once('moveend', () => resolve());
                map.jumpTo(options);
            };
            if (mapIsReady) {
                make();
            } else {
                map.once('load', make);
            }
        });
    }

    export function flyTo(options: CameraOptions & AnimationOptions): Promise<void> {
        return new Promise(resolve => {
            const make = () => {
                map.once('moveend', () => resolve());
                map.flyTo(options);
            };
            if (mapIsReady) {
                make();
            } else {
                map.once('load', make);
            }
        });
    }

    export function fitBounds(bounds: LngLatBoundsLike): void {
        const make = () => {
            map.fitBounds(bounds, {
                padding: 50
            });
        };
        if (mapIsReady) {
            make();
        } else {
            map.once('load', make);
        }
    }

</script>

<style lang="scss">

    .map {
        background-color: #eee;
        height: 100%;
        overflow: hidden;
    }

    .map-container {
        height: 100%;
    }

</style>

<svelte:options accessors />

<div class="map">
    <div class="map-container" bind:this={mapContainer}></div>
    <slot></slot>
</div>
